/**
 * Auth service — pure async functions for gateway auth API.
 *
 * Uses a raw axios instance (not the `api` instance) to avoid circular
 * dependency: the api instance needs a token, and this module provides it.
 */

import { config } from '@/config/environment';
import axios from 'axios';

// ---------------------------------------------------------------------------
// Types (match gateway_service schemas exactly)
// ---------------------------------------------------------------------------

export interface LoginRequest {
  /**
   * The workspace to sign in to. Optional (XC-TEAM-INVITES T5): without it the password picks the
   * account, and someone in several workspaces is asked to choose (``workspace_choice_required``).
   */
  tenant_slug?: string;
  email: string;
  password: string;
  /** C11 second step: a 6-digit authenticator code (two-step accounts only). */
  totp_code?: string;
  /** C11 second step: one unused backup code, instead of totp_code. */
  backup_code?: string;
  /** C11: the trusted-browser token an earlier sign-in returned; waives the code. */
  trust_token?: string;
  /** C11: ask for a trusted-browser token (skips the code here for 30 days). */
  remember_browser?: boolean;
}

export interface SignupRequest {
  tenant_name: string;
  tenant_slug: string;
  email: string;
  password: string;
  display_name: string;
}

/** Mirrors askwingo_shared.models.auth.Role enum values. */
export type UserRole = 'owner' | 'admin' | 'member' | 'viewer';

export interface AuthUser {
  user_id: string;
  email: string;
  display_name: string;
  role: UserRole;
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  /** ATTR-1 user-tied canonical_entity. Populated lazily via /api/v1/users/me;
   * absent on the initial JWT payload (the gateway hasn't started embedding
   * it yet). The sidebar's "My Profile" link uses this to deep-link to the
   * EMP page (EMP-GAP-7). */
  canonical_entity_id?: string | null;
  /** CASA 1.1.2: the user still holds an admin-set initial password. Every
   * gateway call except change-password and logout answers 403 until they
   * replace it, so the app shows only the change-password screen. */
  password_change_required?: boolean;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface TokenResponse {
  token: string;
  token_type: string;
  expires_in: number;
}

export interface LoginResponse {
  auth: TokenResponse;
  user: AuthUser;
  /** C11: set when remember_browser was asked for and a code was given. */
  mfa_trust_token?: string | null;
}

// ---------------------------------------------------------------------------
// Two-step sign-in (C11) — mirrors gateway schemas/auth.py Mfa* models
// ---------------------------------------------------------------------------

export interface MfaStatus {
  enrolled: boolean;
  required_for_role: boolean;
  /** Deployment setting ("off" | "required"); drives copy, never shown raw. */
  enforcement: string;
  backup_codes_left: number;
}

export interface MfaEnrollStart {
  /** Base32 secret for manual entry into an authenticator app. */
  secret: string;
  /** otpauth:// URI, rendered client-side as a QR code. */
  otpauth_uri: string;
}

export interface MfaBackupCodes {
  /** Shown exactly once; only digests are stored server-side. */
  backup_codes: string[];
  /**
   * A fresh session: set when enrollment ran on an enrollment-only token (it finishes sign-in),
   * and when it REPLACED an authenticator, which ends every session issued before it.
   */
  login?: LoginResponse | null;
}

/**
 * What a refused login is asking for next. The gateway answers a correct
 * password on a two-step account with 401 ``detail.code == "mfa_required"``,
 * and an unenrolled owner/admin under enforcement with
 * ``detail.code == "mfa_enrollment_required"`` plus an enrollment-only token.
 */
export type LoginChallenge =
  | { kind: 'mfa_required' }
  | { kind: 'mfa_enrollment_required'; enrollmentToken: string }
  | { kind: 'workspace_choice_required'; workspaces: WorkspaceChoice[] };

/** A workspace this password opens (only those are ever named). */
export interface WorkspaceChoice {
  slug: string;
  name: string;
}

export function loginChallenge(err: unknown): LoginChallenge | null {
  const response = (err as { response?: { status?: number; data?: { detail?: unknown } } })
    ?.response;
  if (response?.status !== 401) return null;
  const detail = response.data?.detail as { code?: unknown; enrollment_token?: unknown } | null;
  if (!detail || typeof detail !== 'object') return null;
  if (detail.code === 'mfa_required') return { kind: 'mfa_required' };
  if (detail.code === 'mfa_enrollment_required' && typeof detail.enrollment_token === 'string') {
    return { kind: 'mfa_enrollment_required', enrollmentToken: detail.enrollment_token };
  }
  const workspaces = (detail as { workspaces?: unknown }).workspaces;
  if (detail.code === 'workspace_choice_required' && Array.isArray(workspaces)) {
    const valid = workspaces.filter(
      (w): w is WorkspaceChoice =>
        !!w && typeof (w as WorkspaceChoice).slug === 'string' && typeof (w as WorkspaceChoice).name === 'string',
    );
    if (valid.length > 0) return { kind: 'workspace_choice_required', workspaces: valid };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Raw client — no interceptors, no auth headers
// ---------------------------------------------------------------------------

const gateway = axios.create({
  baseURL: config.GATEWAY_URL,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

export async function login(req: LoginRequest): Promise<LoginResponse> {
  const { data } = await gateway.post<LoginResponse>('/api/v1/auth/login', req);
  return data;
}

export async function signup(req: SignupRequest): Promise<LoginResponse> {
  const { data } = await gateway.post<LoginResponse>('/api/v1/auth/signup', req);
  return data;
}

export async function refreshToken(token: string): Promise<TokenResponse> {
  const { data } = await gateway.post<TokenResponse>(
    '/api/v1/auth/refresh',
    null,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return data;
}

/** End every session this user holds, server-side (CASA 2.2.1). */
export async function logout(token: string): Promise<void> {
  await gateway.post('/api/v1/auth/logout', null, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

/** Replace the password; returns a fresh login (other sessions end). */
export async function changePassword(
  token: string,
  req: ChangePasswordRequest,
): Promise<LoginResponse> {
  const { data } = await gateway.post<LoginResponse>('/api/v1/auth/change-password', req, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
}

// ---------------------------------------------------------------------------
// Two-step sign-in (C11). ``token`` is a session token, or — for status and
// the enroll routes only — the enrollment-only token a login handed back.
// ---------------------------------------------------------------------------

const bearer = (token: string) => ({ headers: { Authorization: `Bearer ${token}` } });

export async function getMfaStatus(token: string): Promise<MfaStatus> {
  const { data } = await gateway.get<MfaStatus>('/api/v1/auth/mfa/status', bearer(token));
  return data;
}

/** Stage a new authenticator secret. Replacing an existing one needs ``currentCode``. */
export async function startMfaEnrollment(
  token: string,
  currentCode?: string,
): Promise<MfaEnrollStart> {
  const body = currentCode ? { current_code: currentCode } : {};
  const { data } = await gateway.post<MfaEnrollStart>(
    '/api/v1/auth/mfa/enroll/start',
    body,
    bearer(token),
  );
  return data;
}

/** Turn two-step on with a first code; returns the backup codes (once). */
export async function confirmMfaEnrollment(token: string, code: string): Promise<MfaBackupCodes> {
  const { data } = await gateway.post<MfaBackupCodes>(
    '/api/v1/auth/mfa/enroll/confirm',
    { code },
    bearer(token),
  );
  return data;
}

/** Replace every backup code; needs a current authenticator code. */
export async function regenerateBackupCodes(token: string, code: string): Promise<MfaBackupCodes> {
  const { data } = await gateway.post<MfaBackupCodes>(
    '/api/v1/auth/mfa/backup-codes',
    { code },
    bearer(token),
  );
  return data;
}

/**
 * Remove a teammate's authenticator (lost phone); 204 on success. Needs the
 * manage-users permission (owner/admin). The gateway refuses (403) resetting
 * yourself, and an admin resetting an owner.
 */
export async function resetUserMfa(token: string, userId: string): Promise<void> {
  await gateway.post(
    `/api/v1/auth/mfa/reset/${encodeURIComponent(userId)}`,
    null,
    bearer(token),
  );
}

// ---------------------------------------------------------------------------
// Team (owner/admin) — mirrors gateway schemas/users.py UserResponse
// ---------------------------------------------------------------------------

export interface TeamMember {
  id: string;
  email: string;
  display_name: string;
  role: UserRole;
  is_active: boolean;
  canonical_entity_id?: string | null;
  /** Whether two-step sign-in is on. Optional: older gateways do not send it. */
  mfa_enrolled?: boolean;
  created_at: string;
  updated_at: string;
}

export interface TeamMemberPage {
  users: TeamMember[];
  /** Every person in the workspace, not just this page (system accounts excluded). */
  total: number;
}

/** GET /api/v1/users (manage-users permission). ``limit`` is at most 100. */
export async function listTeamMembers(
  token: string,
  skip = 0,
  limit = 100,
): Promise<TeamMemberPage> {
  const { data } = await gateway.get<TeamMemberPage>('/api/v1/users', {
    ...bearer(token),
    params: { skip, limit },
  });
  return data;
}

// ---------------------------------------------------------------------------
// Dev auth — cascade: gateway login → dev-token → offline fallback
// ---------------------------------------------------------------------------

/** Dev credentials (matches seed data in all environments). */
const DEV_CREDS: LoginRequest = {
  tenant_slug: 'gwh-mfg',
  email: 'estevan.mccalley@gmail.com',
  password: 'dev123',
};

const DEV_USER: AuthUser = {
  user_id: '00000000-0000-0000-0000-000000000001',
  email: 'estevan.mccalley@gmail.com',
  display_name: 'Estevan McCalley',
  role: 'admin',
  tenant_id: '00000000-0000-0000-0000-000000000010',
  tenant_name: 'GWH Mfg Co.',
  tenant_slug: 'gwh-mfg',
};

/** Base64url encode (JWT-safe). */
function b64url(input: string): string {
  return btoa(input).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
}

/**
 * Get a dev token using a cascade of strategies:
 *
 * 1. **Login via gateway** — returns a token signed with the real JWT secret,
 *    so all Cloud Run services accept it. Works in any environment.
 * 2. **Dev-token endpoint** — only available when gateway runs with
 *    ENVIRONMENT=development. Useful for local gateway.
 * 3. **Offline fallback** — builds an unsigned token client-side so the
 *    frontend always loads, even with no network. API calls will 401 but
 *    the UI remains navigable.
 */
export async function getLocalDevToken(): Promise<LoginResponse> {
  // Strategy 1: Login via gateway (works in all environments)
  try {
    return await login(DEV_CREDS);
  } catch { /* gateway unreachable or creds not seeded — try next */ }

  // Strategy 2: Dev-token endpoint (local gateway only)
  try {
    return await fetchDevToken();
  } catch { /* endpoint not registered or gateway down — try next */ }

  // Strategy 3: Offline fallback — UI loads, API calls will fail gracefully
  console.warn(
    '[auth] Gateway unreachable — using offline dev token. ' +
    'API calls to Cloud Run will return 401.',
  );
  const header = b64url(JSON.stringify({ alg: 'none', typ: 'JWT' }));
  const payload = b64url(
    JSON.stringify({
      sub: DEV_USER.user_id,
      tenant_id: DEV_USER.tenant_id,
      email: DEV_USER.email,
      role: DEV_USER.role,
      display_name: DEV_USER.display_name,
      tenant_name: DEV_USER.tenant_name,
      tenant_slug: DEV_USER.tenant_slug,
      permissions: ['*'],
      exp: Math.floor(Date.now() / 1000) + 86400,
      iat: Math.floor(Date.now() / 1000),
    }),
  );
  const token = `${header}.${payload}.offline`;

  return {
    auth: { token, token_type: 'bearer', expires_in: 86400 },
    user: DEV_USER,
  };
}

export async function fetchDevToken(persona: string = 'developer'): Promise<LoginResponse> {
  const { data } = await gateway.post<LoginResponse>(
    `/api/v1/auth/dev-token?persona=${persona}`,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Single-use links (XC-TEAM-INVITES Phase 1): open routes, no session yet
// ---------------------------------------------------------------------------

// A link is always sent in the request BODY, never a path (request logs, trace spans and Referer
// all carry the path; /ship security r1 C1). The email puts it in the page's URL fragment.

/** What an invite link shows; anything but ``live`` carries nothing else. */
export async function readInvite(token: string): Promise<unknown> {
  const { data } = await gateway.post<unknown>('/api/v1/auth/invite/read', { token });
  return data;
}

export interface AcceptInviteResponse {
  /** A session, or (an Owner or Admin under two-step enforcement) only the enrollment token. */
  login: LoginResponse | null;
  enrollment_token: string | null;
}

export async function acceptInvite(
  token: string,
  body: { display_name: string; password: string },
): Promise<AcceptInviteResponse> {
  const { data } = await gateway.post<AcceptInviteResponse>('/api/v1/auth/invite/accept', {
    token,
    ...body,
  });
  return data;
}

/** Always answers 202, whatever the email: the page says the same thing either way. */
export async function forgotPassword(email: string): Promise<void> {
  await gateway.post('/api/v1/auth/password/forgot', { email });
}

export async function resetPassword(token: string, password: string): Promise<void> {
  await gateway.post('/api/v1/auth/password/reset', { token, password });
}

/** The state a refused link reports (410 ``detail.state``), or null for any other failure. */
export function linkRefusal(err: unknown): 'invalid' | 'expired' | 'used' | 'cancelled' | null {
  const response = (err as { response?: { status?: number; data?: { detail?: unknown } } })
    ?.response;
  if (response?.status !== 410) return null;
  const state = (response.data?.detail as { state?: unknown } | undefined)?.state;
  return state === 'invalid' || state === 'expired' || state === 'used' || state === 'cancelled'
    ? state
    : 'invalid';
}

// ---------------------------------------------------------------------------
// Sign in with Google (XC-GOOGLE-SIGNIN). The browser goes to Google and back by redirect; the
// gateway alone talks to Google. Nothing here holds a Google token.
// ---------------------------------------------------------------------------

/** Which ways in the sign-in page offers. */
export interface AuthMethods {
  password: boolean;
  google: boolean;
}

export async function getAuthMethods(): Promise<AuthMethods> {
  const { data } = await gateway.get<AuthMethods>('/api/v1/auth/methods');
  return data;
}

/** Google's address, and the flow pass this tab keeps until Google sends the person back. */
export interface GoogleStart {
  authorization_url: string;
  flow: string;
}

export async function startGoogle(redirectUri: string, inviteToken?: string): Promise<GoogleStart> {
  const body: { redirect_uri: string; invite_token?: string } = { redirect_uri: redirectUri };
  if (inviteToken) body.invite_token = inviteToken;
  const { data } = await gateway.post<GoogleStart>('/api/v1/auth/google/start', body);
  return data;
}

/** A session, or (an Owner or Admin not yet enrolled) only the enrollment token. */
export interface GoogleSignInResult {
  login: LoginResponse | null;
  enrollment_token: string | null;
}

export async function completeGoogle(body: {
  flow: string;
  code: string;
  state: string;
}): Promise<GoogleSignInResult> {
  const { data } = await gateway.post<GoogleSignInResult>('/api/v1/auth/google/complete', body);
  return data;
}

export interface GoogleFinishRequest {
  google_pass: string;
  tenant_slug?: string;
  totp_code?: string;
  backup_code?: string;
  trust_token?: string;
  remember_browser?: boolean;
}

export async function finishGoogle(body: GoogleFinishRequest): Promise<GoogleSignInResult> {
  const { data } = await gateway.post<GoogleSignInResult>('/api/v1/auth/google/finish', body);
  return data;
}

/** What a refused Google step asks for next, or why it stopped (note §4's cards). */
export type GoogleStep =
  | { kind: 'workspace_choice_required'; googlePass: string; workspaces: WorkspaceChoice[] }
  | { kind: 'mfa_required'; googlePass: string; email: string; tenantSlug: string }
  | { kind: 'wrong_code' }
  | { kind: 'no_account'; email: string }
  | { kind: 'email_unconfirmed'; email: string }
  | { kind: 'invite_email_mismatch'; invitedEmail: string; googleEmail: string }
  | { kind: 'link'; state: 'invalid' | 'expired' | 'used' | 'cancelled' }
  | { kind: 'flow_expired' }
  | { kind: 'pass_expired' }
  | { kind: 'temporary_password_expired' }
  | { kind: 'failed' };

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

export function googleStep(err: unknown): GoogleStep {
  const response = (err as { response?: { status?: number; data?: { detail?: unknown } } })
    ?.response;
  const link = linkRefusal(err);
  if (link) return { kind: 'link', state: link };
  const detail = response?.data?.detail;
  if (detail === 'Invalid credentials') return { kind: 'wrong_code' };
  if (!detail || typeof detail !== 'object') return { kind: 'failed' };
  const d = detail as Record<string, unknown>;
  switch (d.code) {
    case 'workspace_choice_required': {
      const choice = loginChallenge(err);
      return choice?.kind === 'workspace_choice_required' && text(d.google_pass)
        ? { kind: 'workspace_choice_required', googlePass: text(d.google_pass), workspaces: choice.workspaces }
        : { kind: 'failed' };
    }
    case 'mfa_required':
      return text(d.google_pass) && text(d.tenant_slug)
        ? {
            kind: 'mfa_required',
            googlePass: text(d.google_pass),
            email: text(d.email),
            tenantSlug: text(d.tenant_slug),
          }
        : { kind: 'failed' };
    case 'google_no_account':
      return { kind: 'no_account', email: text(d.email) };
    case 'google_email_unconfirmed':
      return { kind: 'email_unconfirmed', email: text(d.email) };
    case 'google_invite_email_mismatch':
      return {
        kind: 'invite_email_mismatch',
        invitedEmail: text(d.invited_email),
        googleEmail: text(d.google_email),
      };
    case 'google_flow_expired':
      return { kind: 'flow_expired' };
    case 'google_pass_expired':
      return { kind: 'pass_expired' };
    case 'google_temporary_password_expired':
      return { kind: 'temporary_password_expired' };
    default:
      return { kind: 'failed' };
  }
}
